import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import { useTheme } from '../store/ThemeContext';
import api from '../services/api';
import {
  getMFADevices,
  deleteMFADevice,
  getTenantMFAPolicy,
  updateTenantMFAPolicy,
  getTenantEmailAccounts,
  getEmailAccountOAuthUrl,
  createTenantEmailAccount,
  updateTenantEmailAccount,
  deleteTenantEmailAccount,
  testSMTPConnection,
} from '../services/api';
import { MFAEnrollmentModal } from '../components/MFAEnrollmentModal';
import { Building, Layers, User, Lock, Package, CreditCard, Wallet, Shield, Trash2, AlertCircle, Mail, Plus, Check, X, ChevronRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import './SettingsPage.css';

/**
 * Settings page with tabs for Tenant Settings, Plans & Modules, My Profile, and Security.
 * RBAC enforced: Admin-only for tenant/plans, self-only for profile/security.
 */
export const SettingsPage = ({ initialTab }) => {
  const { user, tenant, refreshEntitlements } = useEntitlements();
  const { theme, changeTheme } = useTheme();
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  const tabIds = ['tenant', 'plans', 'payment-methods', 'email-accounts', 'profile', 'security'];

  // Handle tab from URL or initialTab prop
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (initialTab && tabIds.includes(initialTab)) setActiveTab(initialTab);
    else if (tabParam && tabIds.includes(tabParam)) setActiveTab(tabParam);
  }, [initialTab]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const tabs = [
    {
      id: 'tenant',
      label: 'Tenant Settings',
      icon: Building,
      adminOnly: true,
    },
    {
      id: 'plans',
      label: 'Plans & Modules',
      icon: Layers,
      adminOnly: true,
    },
    {
      id: 'payment-methods',
      label: 'Payment Methods',
      icon: Wallet,
      adminOnly: true,
    },
    {
      id: 'email-accounts',
      label: 'Email Accounts',
      icon: Mail,
      adminOnly: true,
    },
    {
      id: 'profile',
      label: 'My Profile',
      icon: User,
      adminOnly: false,
    },
    {
      id: 'security',
      label: 'Security',
      icon: Lock,
      adminOnly: false,
    },
  ];

  const visibleTabs = tabs.filter(tab =>
    (!tab.adminOnly || isAdmin) && (!tab.staffOnly || isStaff)
  );

  // Set default tab if current tab is not visible
  useEffect(() => {
    if (!visibleTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || 'profile');
    }
  }, [isAdmin, activeTab, visibleTabs]);

  return (
    <div className="settings-page">
      <PageHeader
        title="Settings"
        subtitle="Configure account, billing, security, and workspace behavior."
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      <div className="settings-tabs">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="settings-content">
        {activeTab === 'tenant' && (
          <TenantSettingsTab
            tenant={tenant}
            onSuccess={(msg) => {
              setSuccessMessage(msg);
              refreshEntitlements();
            }}
            onError={(err) => setError(err)}
            setLoading={setLoading}
          />
        )}

        {activeTab === 'plans' && (
          <PlansModulesTab
            tenant={tenant}
            onSuccess={(msg) => {
              setSuccessMessage(msg);
              refreshEntitlements();
            }}
            onError={(err) => setError(err)}
            setLoading={setLoading}
          />
        )}

        {activeTab === 'payment-methods' && (
          <PaymentMethodsTab
            onSuccess={(msg) => setSuccessMessage(msg)}
            onError={(err) => setError(err)}
            setLoading={setLoading}
          />
        )}

        {activeTab === 'email-accounts' && (
          <EmailAccountsTab
            onSuccess={(msg) => setSuccessMessage(msg)}
            onError={(err) => setError(err)}
            setLoading={setLoading}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTab
            user={user}
            onSuccess={(msg) => {
              setSuccessMessage(msg);
              refreshEntitlements();
            }}
            onError={(err) => setError(err)}
            setLoading={setLoading}
            theme={theme}
            changeTheme={changeTheme}
          />
        )}

        {activeTab === 'security' && (
          <SecurityTab
            user={user}
            onSuccess={(msg) => {
              setSuccessMessage(msg);
            }}
            onError={(err) => setError(err)}
            setLoading={setLoading}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Email Accounts Tab (Admin-only): connect Google/Microsoft/Yahoo/SMTP, set default, enable/disable, remove.
 */
const EmailAccountsTab = ({ onSuccess, onError, setLoading }) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showSMTPForm, setShowSMTPForm] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    provider: 'smtp',
    email_address: '',
    display_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAccounts = async () => {
    setLoadingList(true);
    try {
      const res = await getTenantEmailAccounts();
      setAccounts(res.data.results ?? res.data ?? []);
    } catch (err) {
      onError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load email accounts');
      setAccounts([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success')) {
      onSuccess('Email account connected successfully.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('oauth_error')) {
      onError(`OAuth error: ${params.get('oauth_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleOAuthConnect = async (provider) => {
    setLoading(true);
    onError(null);
    try {
      const res = await getEmailAccountOAuthUrl(provider);
      const url = res.data?.authorization_url;
      if (url) window.location.href = url;
      else onError('No authorization URL returned');
    } catch (err) {
      onError(err.response?.data?.error || err.response?.data?.detail || 'Failed to start OAuth');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSMTP = async () => {
    setTesting(true);
    onError(null);
    try {
      await testSMTPConnection({
        host: smtpForm.smtp_host,
        port: parseInt(smtpForm.smtp_port, 10),
        username: smtpForm.smtp_username,
        password: smtpForm.smtp_password,
      });
      onSuccess('Connection successful. You can save this account.');
    } catch (err) {
      onError(err.response?.data?.error || 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSMTP = async (e) => {
    e.preventDefault();
    setSaving(true);
    setLoading(true);
    onError(null);
    try {
      await createTenantEmailAccount({
        provider: smtpForm.provider,
        email_address: smtpForm.email_address,
        display_name: smtpForm.display_name,
        smtp_host: smtpForm.smtp_host,
        smtp_port: parseInt(smtpForm.smtp_port, 10),
        smtp_username: smtpForm.smtp_username,
        smtp_password: smtpForm.smtp_password,
      });
      onSuccess('Email account added.');
      setShowSMTPForm(false);
      setSmtpForm({ provider: 'smtp', email_address: '', display_name: '', smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '' });
      fetchAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || (err.response?.data && typeof err.response.data === 'object' ? Object.values(err.response.data).flat().join(', ') : 'Failed to add account');
      onError(msg);
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    setLoading(true);
    onError(null);
    try {
      await updateTenantEmailAccount(id, { is_default: true });
      onSuccess('Default sender updated.');
      fetchAccounts();
    } catch (err) {
      onError(err.response?.data?.error || err.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (acc) => {
    setLoading(true);
    onError(null);
    try {
      await updateTenantEmailAccount(acc.id, { is_enabled: !acc.is_enabled });
      onSuccess(acc.is_enabled ? 'Account disabled.' : 'Account enabled.');
      fetchAccounts();
    } catch (err) {
      onError(err.response?.data?.error || err.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this email account? You will not be able to send from it until reconnected.')) return;
    setLoading(true);
    onError(null);
    try {
      await deleteTenantEmailAccount(id);
      onSuccess('Account removed.');
      fetchAccounts();
    } catch (err) {
      onError(err.response?.data?.error || err.response?.data?.detail || 'Failed to remove');
    } finally {
      setLoading(false);
    }
  };

  const providerLabel = (p) => ({ google: 'Google', microsoft: 'Microsoft', yahoo: 'Yahoo', smtp: 'Custom SMTP' })[p] || p;

  return (
    <div className="settings-tab-content">
      <h2>Email Accounts</h2>
      <p className="settings-description">
        Send emails from your own address (Gmail, Outlook, Yahoo, or custom SMTP). Connect an account to use it for invoices and messages.
      </p>

      {loadingList ? (
        <p>Loading accounts...</p>
      ) : (
        <>
          <div className="settings-form" style={{ marginBottom: '1.5rem' }}>
            {accounts.map((acc) => (
              <div key={acc.id} className="form-group" style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '1rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <strong>{acc.email_address}</strong>
                  {acc.display_name && <span style={{ marginLeft: 8, color: 'var(--color-text-tertiary)' }}>({acc.display_name})</span>}
                  <span style={{ marginLeft: 8, fontSize: '0.9em' }}>{providerLabel(acc.provider)}</span>
                  {acc.is_default && <span style={{ marginLeft: 8, background: 'var(--primary)', color: 'white', padding: '2px 8px', borderRadius: 4 }}>Default</span>}
                  {!acc.is_enabled && <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>Disabled</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!acc.is_default && <button type="button" className="btn btn-secondary" onClick={() => handleSetDefault(acc.id)}>Set default</button>}
                  <button type="button" className="btn btn-secondary" onClick={() => handleToggleEnabled(acc)}>{acc.is_enabled ? 'Disable' : 'Enable'}</button>
                  <button type="button" className="btn btn-danger" onClick={() => handleRemove(acc.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: '1.5rem' }}>Connect account</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button type="button" className="btn btn-primary" onClick={() => handleOAuthConnect('google')}>Connect Google</button>
            <button type="button" className="btn btn-primary" onClick={() => handleOAuthConnect('microsoft')}>Connect Microsoft</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowSMTPForm(!showSMTPForm)}>{showSMTPForm ? 'Cancel' : 'Yahoo / Custom SMTP'}</button>
          </div>

          {showSMTPForm && (
            <form onSubmit={handleSaveSMTP} className="settings-form" style={{ maxWidth: 400 }}>
              <div className="form-group">
                <label>Provider</label>
                <select value={smtpForm.provider} onChange={(e) => setSmtpForm({ ...smtpForm, provider: e.target.value })}>
                  <option value="smtp">Custom SMTP</option>
                  <option value="yahoo">Yahoo</option>
                </select>
              </div>
              <div className="form-group">
                <label>Email address *</label>
                <input type="email" value={smtpForm.email_address} onChange={(e) => setSmtpForm({ ...smtpForm, email_address: e.target.value })} required placeholder="you@example.com" />
              </div>
              <div className="form-group">
                <label>Display name</label>
                <input type="text" value={smtpForm.display_name} onChange={(e) => setSmtpForm({ ...smtpForm, display_name: e.target.value })} placeholder="Your Name" />
              </div>
              <div className="form-group">
                <label>SMTP host *</label>
                <input type="text" value={smtpForm.smtp_host} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })} required placeholder="smtp.example.com" />
              </div>
              <div className="form-group">
                <label>SMTP port *</label>
                <input type="number" value={smtpForm.smtp_port} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: e.target.value })} min={1} max={65535} placeholder="587" />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input type="text" value={smtpForm.smtp_username} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_username: e.target.value })} required placeholder="your@email.com" />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="password" value={smtpForm.smtp_password} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })} required placeholder="App password" />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={handleTestSMTP} disabled={testing || !smtpForm.smtp_host || !smtpForm.smtp_username || !smtpForm.smtp_password}>Test connection</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>Save account</button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Tenant Settings Tab (Admin-only)
 */
const TenantSettingsTab = ({ tenant, onSuccess, onError, setLoading }) => {
  const [loading, setLoadingLocal] = useState(false);
  const [formData, setFormData] = useState({
    firm_name: '',
    email: '',
    company_domain: '',
    billing_email: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: 'US',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: 'US',
  });

  useEffect(() => {
    if (tenant) {
      setFormData({
        firm_name: tenant.firm_name || tenant.name || '',
        email: tenant.email || '',
        company_domain: tenant.company_domain || '',
        billing_email: tenant.billing_email || '',
        billing_address_line1: tenant.billing_address_line1 || '',
        billing_address_line2: tenant.billing_address_line2 || '',
        billing_city: tenant.billing_city || '',
        billing_state: tenant.billing_state || '',
        billing_postal_code: tenant.billing_postal_code || '',
        billing_country: tenant.billing_country || 'US',
      });
    }
  }, [tenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingLocal(true);
    setLoading(true);
    onError(null);

    try {
      await api.patch(`/v1/tenants/${tenant.id}/`, formData);
      onSuccess('Tenant settings updated successfully.');
    } catch (err) {
      const errorMsg = err.response?.data?.detail ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : 'Failed to update tenant settings');
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  return (
    <div className="settings-tab-content">
      <h2>Tenant Settings</h2>
      <p className="settings-description">
        Manage your organization's information and billing details. Only administrators can modify these settings.
      </p>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label>Firm / Business Name *</label>
          <input
            type="text"
            value={formData.firm_name}
            onChange={(e) => setFormData({ ...formData, firm_name: e.target.value })}
            required
            placeholder="Your firm or business name"
          />
        </div>

        <div className="form-group">
          <label>Business Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contact@example.com"
          />
        </div>

        <div className="form-group">
          <label>Company Domain</label>
          <input
            type="text"
            value={formData.company_domain}
            onChange={(e) => setFormData({ ...formData, company_domain: e.target.value })}
            placeholder="company.com"
          />
          <small className="form-help">
            Used for organization identification and future domain-based features. Enter domain without protocol (e.g., company.com).
          </small>
        </div>

        <div className="form-group">
          <label>Billing Email</label>
          <input
            type="email"
            value={formData.billing_email}
            onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
            placeholder="billing@example.com"
          />
        </div>

        <div className="form-section">
          <h3>Billing Address</h3>
          
          <div className="form-group">
            <label>Address Line 1</label>
            <input
              type="text"
              value={formData.billing_address_line1}
              onChange={(e) => setFormData({ ...formData, billing_address_line1: e.target.value })}
              placeholder="Street address"
            />
          </div>

          <div className="form-group">
            <label>Address Line 2</label>
            <input
              type="text"
              value={formData.billing_address_line2}
              onChange={(e) => setFormData({ ...formData, billing_address_line2: e.target.value })}
              placeholder="Suite, unit, etc."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.billing_city}
                onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
                placeholder="City"
              />
            </div>

            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.billing_state}
                onChange={(e) => setFormData({ ...formData, billing_state: e.target.value })}
                placeholder="State"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Postal Code</label>
              <input
                type="text"
                value={formData.billing_postal_code}
                onChange={(e) => setFormData({ ...formData, billing_postal_code: e.target.value })}
                placeholder="ZIP / Postal Code"
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={formData.billing_country}
                onChange={(e) => setFormData({ ...formData, billing_country: e.target.value })}
                placeholder="US"
                maxLength={2}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Plans & Modules Tab (View all, Edit admin-only)
 */
const PlansModulesTab = ({ tenant, onSuccess, onError, setLoading }) => {
  const { hasFeature, user } = useEntitlements();
  const [loading, setLoadingLocal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [moduleEntitlements, setModuleEntitlements] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showModuleConfirm, setShowModuleConfirm] = useState(null);
  const [showPlanConfirm, setShowPlanConfirm] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
    handleRedirectParams();
  }, [tenant]);

  /**
   * Handle success/cancel redirects from Stripe Checkout.
   */
  const handleRedirectParams = () => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('success') === 'true') {
      onSuccess('Payment successful! Your subscription is being activated.');
      // Refresh data to show new subscription
      setTimeout(() => {
        fetchData();
      }, 2000);

      // Clean up URL - keep tab parameter
      const newUrl = window.location.pathname + '?tab=plans';
      window.history.replaceState({}, '', newUrl);
    }

    if (params.get('canceled') === 'true') {
      onError('Payment was canceled. Please try again if you wish to subscribe.');

      // Clean up URL - keep tab parameter
      const newUrl = window.location.pathname + '?tab=plans';
      window.history.replaceState({}, '', newUrl);
    }
  };

  const [allSubscriptions, setAllSubscriptions] = useState([]);

  const fetchData = async () => {
    try {
      setLoadingData(true);
      if (tenant) {
        const subResponse = await api.get('/v1/subscriptions/', {
          params: { tenant: tenant.id },
        });
        const subscriptions = subResponse.data.results || subResponse.data || [];
        setAllSubscriptions(subscriptions);
        
        // Find base subscription (not an add-on)
        const baseSubscription = subscriptions.find(sub => {
          const planMetadata = sub.plan?.metadata || {};
          return !planMetadata.addon_type;
        });
        if (baseSubscription) {
          setSubscription(baseSubscription);
        } else if (subscriptions.length > 0) {
          // Fallback to first subscription if no base found
          setSubscription(subscriptions[0]);
        }

        const entitlementsResponse = await api.get('/v1/entitlements/', {
          params: { tenant: tenant.id },
        });
        setModuleEntitlements(entitlementsResponse.data.results || entitlementsResponse.data || []);
      }

      const plansResponse = await api.get('/v1/subscription-plans/');
      setPlans(plansResponse.data.results || plansResponse.data || []);
    } catch (err) {
      onError(err.response?.data?.detail || 'Failed to load plans and modules');
    } finally {
      setLoadingData(false);
    }
  };

  const handleEnableTax = async () => {
    if (!isAdmin) {
      onError('Only administrators can enable modules.');
      return;
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      // Find or create tax entitlement
      let taxEntitlement = moduleEntitlements.find((e) => e.module === 'tax');

      if (!taxEntitlement && tenant) {
        // Create tax entitlement
        const createResponse = await api.post('/v1/entitlements/', {
          tenant: tenant.id,
          module: 'tax',
          is_enabled: false,
        });
        taxEntitlement = createResponse.data;
      }

      if (taxEntitlement) {
        await api.post(`/v1/entitlements/${taxEntitlement.id}/enable/`);
        onSuccess('Portalr Tax enabled successfully!');
        fetchData();
      }
    } catch (err) {
      onError(err.response?.data?.detail || 'Failed to enable Tax module');
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const handleToggleModule = async (entitlement, confirmed = false) => {
    if (!isAdmin) {
      onError('Only administrators can modify modules.');
      return;
    }

    const action = entitlement.is_enabled ? 'disable' : 'enable';
    const modulePrice = parseFloat(entitlement.monthly_price || 0);
    const moduleName = entitlement.module.charAt(0).toUpperCase() + entitlement.module.slice(1);

    // Show confirmation modal with pricing impact
    if (!confirmed) {
      if (action === 'enable' && modulePrice > 0) {
        setShowModuleConfirm({
          entitlement,
          action,
          message: `Enable ${moduleName}?`,
          pricing: `This will add $${modulePrice.toFixed(2)}/month to your subscription.`,
          warning: action === 'disable' ? 'Disabling this module will remove access to related features.' : null,
        });
        return;
      } else if (action === 'disable') {
        setShowModuleConfirm({
          entitlement,
          action,
          message: `Disable ${moduleName}?`,
          pricing: null,
          warning: 'Disabling this module will remove access to related features. You can re-enable it later.',
        });
        return;
      }
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      await api.post(`/v1/entitlements/${entitlement.id}/${action}/`);
      onSuccess(`Module ${action}d successfully.${modulePrice > 0 && action === 'enable' ? ` Your monthly cost will increase by $${modulePrice.toFixed(2)}.` : ''}`);
      setShowModuleConfirm(null);
      fetchData();
    } catch (err) {
      onError(err.response?.data?.detail || `Failed to ${action} module.`);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const handlePlanChange = async (newPlan, action, confirmed = false) => {
    if (!isAdmin) {
      onError('Only administrators can change plans.');
      return;
    }

    // Validate plan has slug
    if (!newPlan.slug) {
      onError('Plan is missing required information. Please contact support.');
      return;
    }

    // Show confirmation modal
    if (!confirmed) {
      const currentPrice = subscription?.plan ? parseFloat(subscription.plan.price_monthly || 0) : 0;
      const newPrice = parseFloat(newPlan.price_monthly || 0);
      const priceDiff = newPrice - currentPrice;
      
      setShowPlanConfirm({
        plan: newPlan,
        action,
        currentPrice,
        newPrice,
        priceDiff,
      });
      return;
    }

    // Confirmed - proceed with Stripe checkout
    if (!showPlanConfirm) {
      onError('Plan change confirmation data is missing.');
      return;
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      // Determine billing cycle from existing subscription or default to 'monthly'
      const billingCycle = subscription?.billing_cycle || 'monthly';

      // Create Stripe Checkout Session
      const response = await api.post('/v1/stripe/create-checkout-session', {
        plan_slug: showPlanConfirm.plan.slug,
        billing_cycle: billingCycle,
        return_url: '/settings?tab=plans',
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error
        || err.response?.data?.detail
        || err.message
        || 'Failed to create checkout session. Please try again.';
      onError(errorMessage);
      setShowPlanConfirm(null);
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const handleAddonCheckout = async (addonPlan) => {
    if (!isAdmin) {
      onError('Only administrators can purchase add-ons.');
      return;
    }

    // Check if add-on is already active
    const isActive = activeAddons.some(addon => addon.plan?.id === addonPlan.id);
    if (isActive) {
      onError('This add-on is already active on your account.');
      return;
    }

    // For Tax Client Packs, check if Tax is enabled
    if (addonPlan.metadata?.addon_category === 'tax_clients' && !taxEnabled) {
      onError('Please enable the Tax module first to purchase Tax Client Packs.');
      return;
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      // Determine billing cycle from existing subscription or default to 'monthly'
      const billingCycle = subscription?.billing_cycle || 'monthly';

      // Create Stripe Checkout Session
      const response = await api.post('/v1/stripe/create-checkout-session', {
        plan_slug: addonPlan.slug,
        billing_cycle: billingCycle,
        return_url: '/settings?tab=plans',
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error
        || err.response?.data?.detail
        || err.message
        || 'Failed to create checkout session. Please try again.';
      onError(errorMessage);
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="settings-loading">Loading plans and modules...</div>;
  }

  // Check if Tax is enabled
  const taxEntitlement = moduleEntitlements.find((e) => e.module === 'tax');
  const taxEnabled = taxEntitlement?.is_enabled || false;

  // Separate plans: base plans, add-ons, and tax plans (ordered: Starter, Professional, Business)
  const CORE_PLAN_ORDER = ['starter', 'professional', 'business'];
  const corePlans = plans
    .filter((plan) => {
      const metadata = plan.metadata || {};
      return !metadata.module && !metadata.addon_type;
    })
    .sort((a, b) => {
      const idxA = CORE_PLAN_ORDER.indexOf((a.slug || '').toLowerCase());
      const idxB = CORE_PLAN_ORDER.indexOf((b.slug || '').toLowerCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  const taxPlans = plans.filter((plan) => plan.metadata?.module === 'tax');
  
  // Filter add-ons by category
  const addonPlans = plans.filter((plan) => plan.metadata?.addon_type);
  const clientPackAddons = addonPlans.filter((plan) => plan.metadata?.addon_category === 'core_clients');
  const taxClientPackAddons = addonPlans.filter((plan) => plan.metadata?.addon_category === 'tax_clients');
  const userSeatAddons = addonPlans.filter((plan) => plan.metadata?.addon_category === 'users');
  const brandingAddons = addonPlans.filter((plan) => plan.metadata?.addon_category === 'branding');
  
  // Get active add-on subscriptions
  const activeAddons = allSubscriptions.filter((sub) => {
    const planMetadata = sub.plan?.metadata || {};
    return planMetadata.addon_type && sub.status === 'active';
  });

  return (
    <div className="settings-tab-content">
      <h2>Plans & Modules</h2>
      <p className="settings-description">
        View your current subscription plan and manage module entitlements. {!isAdmin && 'Only administrators can modify modules.'}
      </p>

      {subscription && (
        <div className="current-plan-card">
          <div className="current-plan-header">
            <CreditCard size={20} />
            <h3>Current Plan</h3>
          </div>
          <div className="plan-info">
            <div>
              <strong>{subscription.plan?.name || 'No Plan'}</strong>
              <p className="plan-status">Status: <span className={`status-badge status-${subscription.status}`}>{subscription.status}</span></p>
            </div>
            {subscription.plan && (
              <div className="plan-price-large">
                {subscription.billing_cycle === 'yearly' 
                  ? `$${parseFloat(subscription.plan.price_yearly || 0).toFixed(2)}/year`
                  : `$${parseFloat(subscription.plan.price_monthly || 0).toFixed(2)}/month`}
              </div>
            )}
          </div>
          {subscription.plan && (
            <div className="plan-cost-breakdown">
              <div className="cost-row">
                <span>Base Plan:</span>
                <strong>
                  {subscription.billing_cycle === 'yearly'
                    ? `$${parseFloat(subscription.plan.price_yearly || 0).toFixed(2)}/year`
                    : `$${parseFloat(subscription.plan.price_monthly || 0).toFixed(2)}/month`}
                </strong>
              </div>
              {moduleEntitlements.filter(e => e.is_enabled && parseFloat(e.monthly_price || 0) > 0).length > 0 && (
                <>
                  <div className="cost-row">
                    <span>Enabled Modules:</span>
                    <div className="module-costs">
                      {moduleEntitlements
                        .filter(e => e.is_enabled && parseFloat(e.monthly_price || 0) > 0)
                        .map((e, idx) => (
                          <span key={e.id} className="module-cost-item">
                            {e.module.charAt(0).toUpperCase() + e.module.slice(1)}: ${parseFloat(e.monthly_price || 0).toFixed(2)}
                            {idx < moduleEntitlements.filter(e => e.is_enabled && parseFloat(e.monthly_price || 0) > 0).length - 1 ? ' + ' : ''}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="cost-row cost-total">
                    <span>Estimated Monthly Total:</span>
                    <strong>
                      ${(
                        parseFloat(subscription.plan.price_monthly || 0) +
                        moduleEntitlements
                          .filter(e => e.is_enabled)
                          .reduce((sum, e) => sum + parseFloat(e.monthly_price || 0), 0)
                      ).toFixed(2)}/month
                    </strong>
                  </div>
                </>
              )}
              {moduleEntitlements.filter(e => e.is_enabled && parseFloat(e.monthly_price || 0) > 0).length === 0 && (
                <div className="cost-row cost-total">
                  <span>Estimated Monthly Total:</span>
                  <strong>
                    ${parseFloat(subscription.plan.price_monthly || 0).toFixed(2)}/month
                  </strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="modules-section">
        <div className="section-header">
          <Package size={20} />
          <h3>Modules</h3>
        </div>
        <div className="modules-list">
          {/* Tax Module - Special handling */}
          {(() => {
            const taxEntitlement = moduleEntitlements.find((e) => e.module === 'tax');

            if (taxEntitlement) {
              const modulePrice = parseFloat(taxEntitlement.monthly_price || 0);
              return (
                <div key={taxEntitlement.id} className="module-card">
                  <div className="module-info">
                    <div className="module-header">
                      <strong>Tax</strong>
                      {modulePrice > 0 && (
                        <span className="module-price">${modulePrice.toFixed(2)}/month</span>
                      )}
                    </div>
                    <p className="module-description">Comprehensive tax preparation and filing solution</p>
                    <span className={`module-status ${taxEntitlement.is_enabled ? 'enabled' : 'disabled'}`}>
                      {taxEntitlement.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {isAdmin ? (
                    <button
                      className={`btn ${taxEntitlement.is_enabled ? 'btn-secondary' : 'btn-primary'}`}
                      onClick={() => handleToggleModule(taxEntitlement)}
                      disabled={loading}
                    >
                      {taxEntitlement.is_enabled ? 'Disable' : 'Enable'}
                    </button>
                  ) : (
                    <button
                      className="btn btn-disabled"
                      disabled
                      title="Admin only"
                    >
                      {taxEntitlement.is_enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  )}
                </div>
              );
            } else {
              // No tax entitlement exists - show CTA
              return (
                <div className="module-card module-cta">
                  <div className="module-info">
                    <div className="module-header">
                      <strong>Portalr Tax</strong>
                    </div>
                    <p className="module-description">Enable tax management and compliance features to access Tax module plans</p>
                  </div>
                  {isAdmin ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleEnableTax}
                      disabled={loading}
                    >
                      Enable Portalr Tax
                    </button>
                  ) : (
                    <button
                      className="btn btn-disabled"
                      disabled
                      title="Admin only"
                    >
                      Enable Tax
                    </button>
                  )}
                </div>
              );
            }
          })()}

          {/* Other Module Entitlements */}
          {moduleEntitlements.filter((e) => e.module !== 'tax').map((entitlement) => {
            const modulePrice = parseFloat(entitlement.monthly_price || 0);
            const moduleName = entitlement.module.charAt(0).toUpperCase() + entitlement.module.slice(1);
            const moduleDescription = entitlement.module === 'accounting'
              ? 'Full-featured accounting and bookkeeping tools'
              : entitlement.module === 'payroll'
              ? 'Complete payroll management system'
              : 'Additional module feature';

            return (
              <div key={entitlement.id} className="module-card">
                <div className="module-info">
                  <div className="module-header">
                    <strong>{moduleName}</strong>
                    {modulePrice > 0 && (
                      <span className="module-price">${modulePrice.toFixed(2)}/month</span>
                    )}
                  </div>
                  {moduleDescription && (
                    <p className="module-description">{moduleDescription}</p>
                  )}
                  <span className={`module-status ${entitlement.is_enabled ? 'enabled' : 'disabled'}`}>
                    {entitlement.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {isAdmin ? (
                  <button
                    className={`btn ${entitlement.is_enabled ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => handleToggleModule(entitlement)}
                    disabled={loading}
                  >
                    {entitlement.is_enabled ? 'Disable' : 'Enable'}
                  </button>
                ) : (
                  <button
                    className="btn btn-disabled"
                    disabled
                    title="Admin only"
                  >
                    {entitlement.is_enabled ? 'Enabled' : 'Disabled'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Core Plans */}
      {corePlans.length > 0 && (
        <div className="available-plans-section">
          <div className="section-header">
            <Layers size={20} />
            <h3>Portalr Core Plans</h3>
          </div>
          <div className="plans-grid">
            {corePlans.map((plan) => {
              const isCurrentPlan = subscription?.plan?.id === plan.id;
              const currentPrice = subscription?.plan ? parseFloat(subscription.plan.price_monthly || 0) : 0;
              const planPrice = parseFloat(plan.price_monthly || 0);
              const isUpgrade = planPrice > currentPrice;
              const isDowngrade = planPrice < currentPrice && currentPrice > 0;
              
              return (
                <div key={plan.id} className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}>
                  {isCurrentPlan && <div className="current-badge">Current Plan</div>}
                  <h4>{plan.name}</h4>
                  <div className="plan-price-large">
                    ${planPrice.toFixed(2)}/month
                    {parseFloat(plan.price_yearly || 0) > 0 && (
                      <span className="plan-yearly"> or ${parseFloat(plan.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {plan.description && (
                    <div className="plan-description">
                      {plan.description.split('\n\n').map((paragraph, idx) => {
                        const trimmed = paragraph.trim();
                        if (!trimmed) return null;
                        
                        // Handle "Best for:" and "What it delivers:" sections with bullet lists
                        if (trimmed.startsWith('Best for:') || trimmed.startsWith('What it delivers:')) {
                          const lines = trimmed.split('\n');
                          const header = lines[0];
                          const items = lines.slice(1).filter(line => line.trim());
                          
                          return (
                            <div key={idx} className="plan-description-section">
                              <strong>{header}</strong>
                              {items.length > 0 && (
                                <ul className="plan-description-list">
                                  {items.map((item, itemIdx) => {
                                    const cleanItem = item.trim().replace(/^[•\-\*]\s*/, '');
                                    return cleanItem ? <li key={itemIdx}>{cleanItem}</li> : null;
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        }
                        
                        // Handle "Ideal outcome:" as a single line with label
                        if (trimmed.startsWith('Ideal outcome:')) {
                          return (
                            <div key={idx} className="plan-description-section">
                              <strong>{trimmed}</strong>
                            </div>
                          );
                        }
                        
                        // Regular paragraphs
                        return <p key={idx}>{trimmed}</p>;
                      })}
                    </div>
                  )}
                  {plan.features && plan.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {plan.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isCurrentPlan ? (
                      <button className="btn btn-disabled" disabled>
                        Current Plan
                      </button>
                    ) : isAdmin ? (
                      <button
                        className={`btn ${isUpgrade ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handlePlanChange(plan, isUpgrade ? 'upgrade' : isDowngrade ? 'downgrade' : 'change')}
                        disabled={loading}
                      >
                        {isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Switch Plan'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-Ons Section */}
      {(clientPackAddons.length > 0 || (taxEnabled && taxClientPackAddons.length > 0) || userSeatAddons.length > 0 || brandingAddons.length > 0) && (
        <div className="available-plans-section">
          <div className="section-header">
            <Package size={20} />
            <h3>Add-Ons & Usage-Based Pricing</h3>
          </div>
          <p className="settings-description">
            Expand your plan with add-ons. Pay only as you grow without upgrading your base plan.
          </p>
          <div className="plans-grid">
            {/* Client Packs (Core) */}
            {clientPackAddons.map((addon) => {
              const isActive = activeAddons.some(a => a.plan?.id === addon.id);
              const planPrice = parseFloat(addon.price_monthly || 0);
              
              return (
                <div key={addon.id} className={`plan-card ${isActive ? 'current-plan' : ''}`}>
                  {isActive && <div className="current-badge">Active</div>}
                  <h4>{addon.name}</h4>
                  <div className="plan-price-large">
                    ${planPrice.toFixed(2)}/month
                    {parseFloat(addon.price_yearly || 0) > 0 && (
                      <span className="plan-yearly"> or ${parseFloat(addon.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {addon.description && (
                    <div className="plan-description">
                      <p>{addon.description}</p>
                    </div>
                  )}
                  {addon.features && addon.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {addon.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isActive ? (
                      <button className="btn btn-disabled" disabled>
                        Active
                      </button>
                    ) : isAdmin ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAddonCheckout(addon)}
                        disabled={loading}
                      >
                        Add to Subscription
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Tax Client Packs - Only show if Tax is enabled */}
            {taxEnabled && taxClientPackAddons.map((addon) => {
              const isActive = activeAddons.some(a => a.plan?.id === addon.id);
              const planPrice = parseFloat(addon.price_monthly || 0);
              
              return (
                <div key={addon.id} className={`plan-card ${isActive ? 'current-plan' : ''}`}>
                  {isActive && <div className="current-badge">Active</div>}
                  <h4>{addon.name}</h4>
                  <div className="plan-price-large">
                    ${planPrice.toFixed(2)}/month
                    {parseFloat(addon.price_yearly || 0) > 0 && (
                      <span className="plan-yearly"> or ${parseFloat(addon.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {addon.description && (
                    <div className="plan-description">
                      <p>{addon.description}</p>
                    </div>
                  )}
                  {addon.features && addon.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {addon.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isActive ? (
                      <button className="btn btn-disabled" disabled>
                        Active
                      </button>
                    ) : isAdmin ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAddonCheckout(addon)}
                        disabled={loading}
                      >
                        Add to Subscription
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* User Seats */}
            {userSeatAddons.map((addon) => {
              const isActive = activeAddons.some(a => a.plan?.id === addon.id);
              const planPrice = parseFloat(addon.price_monthly || 0);
              const isBulk = addon.metadata?.bulk_pricing;
              
              return (
                <div key={addon.id} className={`plan-card ${isActive ? 'current-plan' : ''}`}>
                  {isActive && <div className="current-badge">Active</div>}
                  <h4>{addon.name}</h4>
                  <div className="plan-price-large">
                    {isBulk ? `$${planPrice.toFixed(2)}/user/month` : `$${planPrice.toFixed(2)}/month`}
                    {parseFloat(addon.price_yearly || 0) > 0 && !isBulk && (
                      <span className="plan-yearly"> or ${parseFloat(addon.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {addon.description && (
                    <div className="plan-description">
                      <p>{addon.description}</p>
                    </div>
                  )}
                  {addon.features && addon.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {addon.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isActive ? (
                      <button className="btn btn-disabled" disabled>
                        Active
                      </button>
                    ) : isAdmin ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAddonCheckout(addon)}
                        disabled={loading}
                      >
                        {isBulk ? 'Contact Sales' : 'Add to Subscription'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Branding Add-ons */}
            {brandingAddons.map((addon) => {
              const isActive = activeAddons.some(a => a.plan?.id === addon.id);
              const planPrice = parseFloat(addon.price_monthly || 0);
              const isFeatured = addon.metadata?.addon_value === 'white_label';
              
              return (
                <div key={addon.id} className={`plan-card ${isActive ? 'current-plan' : ''} ${isFeatured ? 'current-plan' : ''}`}>
                  {isActive && <div className="current-badge">Active</div>}
                  {isFeatured && !isActive && <div className="current-badge" style={{background: 'var(--color-accent)', opacity: 0.8}}>Most Popular</div>}
                  <h4>{addon.name}</h4>
                  <div className="plan-price-large">
                    ${planPrice.toFixed(2)}/month
                    {parseFloat(addon.price_yearly || 0) > 0 && (
                      <span className="plan-yearly"> or ${parseFloat(addon.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {addon.description && (
                    <div className="plan-description">
                      <p>{addon.description}</p>
                    </div>
                  )}
                  {addon.features && addon.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {addon.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isActive ? (
                      <button className="btn btn-disabled" disabled>
                        Active
                      </button>
                    ) : isAdmin ? (
                      <button
                        className={`btn ${isFeatured ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleAddonCheckout(addon)}
                        disabled={loading}
                      >
                        Add to Subscription
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tax Plans - Only show if Tax is enabled */}
      {taxEnabled && taxPlans.length > 0 && (
        <div className="available-plans-section">
          <div className="section-header">
            <Layers size={20} />
            <h3>Portalr Tax Plans</h3>
          </div>
          <div className="plans-grid">
            {taxPlans.map((plan) => {
              const isCurrentPlan = subscription?.plan?.id === plan.id;
              const currentPrice = subscription?.plan ? parseFloat(subscription.plan.price_monthly || 0) : 0;
              const planPrice = parseFloat(plan.price_monthly || 0);
              const isUpgrade = planPrice > currentPrice;
              const isDowngrade = planPrice < currentPrice && currentPrice > 0;

              return (
                <div key={plan.id} className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}>
                  {isCurrentPlan && <div className="current-badge">Current Plan</div>}
                  <h4>{plan.name}</h4>
                  <div className="plan-price-large">
                    ${planPrice.toFixed(2)}/month
                    {parseFloat(plan.price_yearly || 0) > 0 && (
                      <span className="plan-yearly"> or ${parseFloat(plan.price_yearly || 0).toFixed(2)}/year</span>
                    )}
                  </div>
                  {plan.description && (
                    <div className="plan-description">
                      {plan.description.split('\n\n').map((paragraph, idx) => {
                        const trimmed = paragraph.trim();
                        if (!trimmed) return null;

                        // Handle "Best for:" and "What it delivers:" sections with bullet lists
                        if (trimmed.startsWith('Best for:') || trimmed.startsWith('What it delivers:')) {
                          const lines = trimmed.split('\n');
                          const header = lines[0];
                          const items = lines.slice(1).filter(line => line.trim());

                          return (
                            <div key={idx} className="plan-description-section">
                              <strong>{header}</strong>
                              {items.length > 0 && (
                                <ul className="plan-description-list">
                                  {items.map((item, itemIdx) => {
                                    const cleanItem = item.trim().replace(/^[•\-\*]\s*/, '');
                                    return cleanItem ? <li key={itemIdx}>{cleanItem}</li> : null;
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        }

                        // Handle "Ideal outcome:" as a single line with label
                        if (trimmed.startsWith('Ideal outcome:')) {
                          return (
                            <div key={idx} className="plan-description-section">
                              <strong>{trimmed}</strong>
                            </div>
                          );
                        }

                        // Regular paragraphs
                        return <p key={idx}>{trimmed}</p>;
                      })}
                    </div>
                  )}
                  {plan.features && plan.features.length > 0 && (
                    <div className="plan-features-section">
                      <strong>Features:</strong>
                      <ul className="plan-features">
                        {plan.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="plan-actions">
                    {isCurrentPlan ? (
                      <button className="btn btn-disabled" disabled>
                        Current Plan
                      </button>
                    ) : isAdmin ? (
                      <button
                        className={`btn ${isUpgrade ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handlePlanChange(plan, isUpgrade ? 'upgrade' : isDowngrade ? 'downgrade' : 'change')}
                        disabled={loading}
                      >
                        {isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Switch Plan'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        title="Admin only"
                      >
                        View Only
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Module Confirmation Modal */}
      {showModuleConfirm && (
        <div className="modal-overlay" onClick={() => setShowModuleConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{showModuleConfirm.message}</h3>
            {showModuleConfirm.pricing && (
              <p className="modal-pricing">{showModuleConfirm.pricing}</p>
            )}
            {showModuleConfirm.warning && (
              <p className="modal-warning">{showModuleConfirm.warning}</p>
            )}
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModuleConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleToggleModule(showModuleConfirm.entitlement, true)}
                disabled={loading}
              >
                {showModuleConfirm.action === 'enable' ? 'Enable Module' : 'Disable Module'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Confirmation Modal */}
      {showPlanConfirm && (
        <div className="modal-overlay" onClick={() => setShowPlanConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Change Plan to {showPlanConfirm.plan.name}?</h3>
            <div className="modal-pricing-breakdown">
              <div className="pricing-row">
                <span>Current Plan:</span>
                <strong>${showPlanConfirm.currentPrice.toFixed(2)}/month</strong>
              </div>
              <div className="pricing-row">
                <span>New Plan:</span>
                <strong>${showPlanConfirm.newPrice.toFixed(2)}/month</strong>
              </div>
              {showPlanConfirm.priceDiff !== 0 && (
                <div className={`pricing-row pricing-diff ${showPlanConfirm.priceDiff > 0 ? 'increase' : 'decrease'}`}>
                  <span>Monthly Change:</span>
                  <strong>
                    {showPlanConfirm.priceDiff > 0 ? '+' : ''}${Math.abs(showPlanConfirm.priceDiff).toFixed(2)}/month
                  </strong>
                </div>
              )}
            </div>
            {showPlanConfirm.action === 'downgrade' && (
              <p className="modal-warning">
                Downgrading your plan may affect access to certain features. You can upgrade again at any time.
              </p>
            )}
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPlanConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handlePlanChange(showPlanConfirm.plan, showPlanConfirm.action, true)}
                disabled={loading}
              >
                Confirm {showPlanConfirm.action === 'upgrade' ? 'Upgrade' : showPlanConfirm.action === 'downgrade' ? 'Downgrade' : 'Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Payment Methods Tab (Admin-only)
 */
const PaymentMethodsTab = ({ onSuccess, onError, setLoading }) => {
  const { tenant } = useEntitlements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoadingLocal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    type: 'stripe',
    display_name: '',
    instructions: '',
    external_reference: '',
    is_enabled: true,
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoadingLocal(true);
    try {
      const response = await api.get('/v1/tenant/payment-methods/');
      setPaymentMethods(response.data.results || response.data || []);
    } catch (err) {
      onError('Failed to load payment methods.');
    } finally {
      setLoadingLocal(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      type: 'stripe',
      display_name: '',
      instructions: '',
      external_reference: '',
      is_enabled: true,
    });
    setEditingMethod(null);
    setShowAddModal(true);
  };

  const handleEdit = (method) => {
    setFormData({
      type: method.type,
      display_name: method.display_name,
      instructions: method.instructions || '',
      external_reference: method.external_reference || '',
      is_enabled: method.is_enabled,
    });
    setEditingMethod(method);
    setShowAddModal(true);
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`Are you sure you want to delete "${method.display_name}"?`)) {
      return;
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      await api.delete(`/v1/tenant/payment-methods/${method.id}/`);
      onSuccess('Payment method deleted successfully.');
      fetchPaymentMethods();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete payment method.';
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (method) => {
    setLoadingLocal(true);
    setLoading(true);
    try {
      await api.patch(`/v1/tenant/payment-methods/${method.id}/`, {
        is_enabled: !method.is_enabled,
      });
      onSuccess(`Payment method ${!method.is_enabled ? 'enabled' : 'disabled'} successfully.`);
      fetchPaymentMethods();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to update payment method.';
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingLocal(true);
    setLoading(true);
    onError(null);

    // Validation
    if (!formData.display_name.trim()) {
      onError('Display name is required.');
      setLoadingLocal(false);
      setLoading(false);
      return;
    }

    if (formData.type !== 'stripe' && !formData.instructions.trim()) {
      onError('Instructions are required for non-Stripe payment methods.');
      setLoadingLocal(false);
      setLoading(false);
      return;
    }

    try {
      if (editingMethod) {
        await api.patch(`/v1/tenant/payment-methods/${editingMethod.id}/`, formData);
        onSuccess('Payment method updated successfully.');
      } else {
        await api.post('/v1/tenant/payment-methods/', formData);
        onSuccess('Payment method added successfully.');
      }
      setShowAddModal(false);
      fetchPaymentMethods();
    } catch (err) {
      const errorMsg = err.response?.data?.detail ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : 'Failed to save payment method.');
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      stripe: 'Stripe',
      zelle: 'Zelle',
      cashapp: 'Cash App',
      square: 'Square',
    };
    return labels[type] || type;
  };

  return (
    <div className="settings-tab-content">
      <div className="settings-section-header">
        <h2>Payment Methods</h2>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Wallet size={18} />
          Add Payment Method
        </button>
      </div>
      <p className="settings-description">
        Configure payment methods that clients can use to pay invoices. Stripe payments are processed automatically, while manual methods (Zelle, Cash App, Square) require clients to follow instructions.
      </p>

      {loading && paymentMethods.length === 0 ? (
        <div className="loading">Loading payment methods...</div>
      ) : paymentMethods.length === 0 ? (
        <div className="portalr-emptyState">
          <div className="portalr-emptyStateIcon" aria-hidden="true">
            <Wallet size={20} />
          </div>
          <div className="portalr-emptyStateTitle">No payment methods configured</div>
          <p className="portalr-emptyStateDesc">
            Add a payment method so you can collect invoice payments smoothly.
          </p>
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="portalr-btn portalr-btn--primary"
              onClick={handleAdd}
            >
              Add payment method
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <div className="payment-methods-list">
          {paymentMethods.map((method) => (
            <div key={method.id} className="payment-method-card">
              <div className="payment-method-header">
                <div className="payment-method-info">
                  <h3>{method.display_name}</h3>
                  <span className="payment-method-type">{getTypeLabel(method.type)}</span>
                </div>
                <div className="payment-method-actions">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={method.is_enabled}
                      onChange={() => handleToggleEnabled(method)}
                      disabled={loading}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEdit(method)}
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(method)}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {method.instructions && (
                <div className="payment-method-instructions">
                  <strong>Instructions:</strong>
                  <p>{method.instructions}</p>
                </div>
              )}
              {method.external_reference && (
                <div className="payment-method-reference">
                  <strong>Reference:</strong> {method.external_reference}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-group">
                <label>Payment Method Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  disabled={!!editingMethod}
                >
                  <option value="stripe">Stripe (Online Card/ACH)</option>
                  <option value="zelle">Zelle (Manual)</option>
                  <option value="cashapp">Cash App (Manual)</option>
                  <option value="square">Square (Manual)</option>
                </select>
                {editingMethod && (
                  <small className="form-help">Payment method type cannot be changed after creation.</small>
                )}
              </div>

              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder={formData.type === 'stripe' ? 'Stripe' : `e.g., ${getTypeLabel(formData.type)} – ${tenant?.name || 'Your Company'}`}
                  required
                />
                <small className="form-help">Name shown to clients on invoices.</small>
              </div>

              {formData.type !== 'stripe' && (
                <>
                  <div className="form-group">
                    <label>Instructions *</label>
                    <textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      placeholder="e.g., Send payment to zelle@example.com or use phone number 555-123-4567"
                      rows={4}
                      required
                    />
                    <small className="form-help">Payment instructions shown to clients.</small>
                  </div>

                  {formData.type === 'zelle' && (
                    <div className="form-group">
                      <label>Zelle Recipient Info</label>
                      <input
                        type="text"
                        value={formData.external_reference}
                        onChange={(e) => setFormData({ ...formData, external_reference: e.target.value })}
                        placeholder="Email or phone number"
                      />
                    </div>
                  )}

                  {formData.type === 'cashapp' && (
                    <div className="form-group">
                      <label>Cash App Handle</label>
                      <input
                        type="text"
                        value={formData.external_reference}
                        onChange={(e) => setFormData({ ...formData, external_reference: e.target.value })}
                        placeholder="$yourhandle"
                      />
                    </div>
                  )}

                  {formData.type === 'square' && (
                    <div className="form-group">
                      <label>Payment Link or POS Reference</label>
                      <input
                        type="text"
                        value={formData.external_reference}
                        onChange={(e) => setFormData({ ...formData, external_reference: e.target.value })}
                        placeholder="Payment link URL or POS reference"
                      />
                    </div>
                  )}
                </>
              )}

              {formData.type === 'stripe' && (
                <div className="form-group">
                  <label>Stripe Connection</label>
                  <div className="stripe-connection-status">
                    <span className="status-badge status-success">Connected</span>
                    <small className="form-help">Stripe is connected via your tenant account.</small>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <small className="form-help">Disabled payment methods won't appear on invoices.</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingMethod ? 'Update' : 'Add'} Payment Method
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * My Profile Tab (Self-edit only)
 */
const ProfileTab = ({ user, onSuccess, onError, setLoading, theme, changeTheme }) => {
  const [loading, setLoadingLocal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingLocal(true);
    setLoading(true);
    onError(null);

    try {
      await api.patch(`/v1/users/${user.id}/`, formData);
      onSuccess('Profile updated successfully.');
    } catch (err) {
      const errorMsg = err.response?.data?.detail ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : 'Failed to update profile');
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  return (
    <div className="settings-tab-content">
      <h2>My Profile</h2>
      <p className="settings-description">
        Update your personal information. You can only edit your own profile.
      </p>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            disabled
            title="Email cannot be changed. Contact an administrator if you need to change your email."
          />
          <small className="form-help">Email cannot be changed. Contact an administrator if needed.</small>
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        {user?.is_client_user && user?.client?.company_name && (
          <div className="form-group">
            <label>Company Name</label>
            <input
              type="text"
              value={user.client.company_name}
              disabled
              title="Company name is managed by your organization"
            />
            <small className="form-help">Company name is managed by your organization.</small>
          </div>
        )}

        <div className="form-section">
          <h3>Appearance</h3>
          <div className="form-group">
            <label>Theme Preference</label>
            <select
              value={theme}
              onChange={(e) => {
                const newTheme = e.target.value;
                // Apply theme immediately using ThemeContext
                changeTheme(newTheme);
              }}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto (System Default)</option>
            </select>
            <small className="form-help">
              Choose your preferred theme. Auto will follow your operating system preference.
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Security Tab (Password reset, MFA setup)
 */
const SecurityTab = ({ user, onSuccess, onError, setLoading }) => {
  const { user: currentUser } = useEntitlements();
  const isAdmin = user?.role === 'admin';
  
  const [loading, setLoadingLocal] = useState(false);
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordStrength, setPasswordStrength] = useState('');
  
  // MFA state
  const [mfaDevices, setMfaDevices] = useState([]);
  const [mfaPolicy, setMfaPolicy] = useState(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [policyFormData, setPolicyFormData] = useState({
    mfa_required_for_admins: false,
    mfa_required_for_users: false,
    mfa_grace_period_days: 0,
  });

  useEffect(() => {
    fetchMFADevices();
    if (isAdmin) {
      fetchMFAPolicy();
    }
  }, [isAdmin]);

  const fetchMFADevices = async () => {
    try {
      const response = await getMFADevices();
      const devices = response.data.results || response.data || [];
      setMfaDevices(devices);
    } catch (err) {
      console.error('Failed to fetch MFA devices:', err);
      setMfaDevices([]); // Ensure it's always an array
    }
  };

  const fetchMFAPolicy = async () => {
    try {
      const response = await getTenantMFAPolicy();
      setMfaPolicy(response.data);
      setPolicyFormData({
        mfa_required_for_admins: response.data.mfa_required_for_admins || false,
        mfa_required_for_users: response.data.mfa_required_for_users || false,
        mfa_grace_period_days: response.data.mfa_grace_period_days || 0,
      });
    } catch (err) {
      console.error('Failed to fetch MFA policy:', err);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this MFA device? You will need to enroll again to use MFA.')) {
      return;
    }

    setMfaLoading(true);
    try {
      await deleteMFADevice(deviceId);
      onSuccess('MFA device deleted successfully.');
      fetchMFADevices();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to delete MFA device.';
      onError(errorMsg);
    } finally {
      setMfaLoading(false);
    }
  };

  const handlePolicyUpdate = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setLoading(true);
    onError(null);

    try {
      await updateTenantMFAPolicy(policyFormData);
      onSuccess('MFA policy updated successfully.');
      fetchMFAPolicy();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to update MFA policy.';
      onError(errorMsg);
    } finally {
      setMfaLoading(false);
      setLoading(false);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    setPasswordStrength(labels[strength - 1] || '');
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, new_password: value });
    checkPasswordStrength(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    onError(null);

    if (formData.new_password !== formData.confirm_password) {
      onError('New passwords do not match.');
      return;
    }

    if (formData.new_password.length < 8) {
      onError('Password must be at least 8 characters long.');
      return;
    }

    setLoadingLocal(true);
    setLoading(true);
    try {
      await api.post('/v1/auth/reset-password', {
        old_password: formData.old_password,
        new_password: formData.new_password,
      });
      onSuccess('Password changed successfully.');
      setFormData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
      setPasswordStrength('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to change password.';
      onError(errorMsg);
    } finally {
      setLoadingLocal(false);
      setLoading(false);
    }
  };

  return (
    <div className="settings-tab-content">
      <h2>Security</h2>
      <p className="settings-description">
        Manage your account security settings including password and multi-factor authentication.
      </p>

      {user?.password_reset_required && (
        <div className="alert alert-warning">
          <strong>Password Reset Required:</strong> You must reset your password before you can access all features.
        </div>
      )}

      {/* Password Change Section */}
      <div className="form-section">
        <h3>Change Password</h3>
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label>Current Password *</label>
            <input
              type="password"
              value={formData.old_password}
              onChange={(e) => setFormData({ ...formData, old_password: e.target.value })}
              required
              placeholder="Enter your current password"
            />
          </div>

          <div className="form-group">
            <label>New Password *</label>
            <input
              type="password"
              value={formData.new_password}
              onChange={handlePasswordChange}
              required
              placeholder="Enter your new password"
              minLength={8}
            />
            {passwordStrength && (
              <small className={`form-help password-strength ${passwordStrength.toLowerCase().replace(' ', '-')}`}>
                Password strength: {passwordStrength}
              </small>
            )}
            <small className="form-help">
              Password must be at least 8 characters long and contain at least one letter and one number.
            </small>
          </div>

          <div className="form-group">
            <label>Confirm New Password *</label>
            <input
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
              placeholder="Confirm your new password"
              minLength={8}
            />
            {formData.new_password && formData.confirm_password && formData.new_password !== formData.confirm_password && (
              <small className="form-help error">Passwords do not match.</small>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Tenant MFA Policy Section (Admin-only) */}
      {isAdmin && (
        <div className="form-section">
          <h3>Tenant MFA Policy</h3>
          <p className="settings-description">
            Configure multi-factor authentication requirements for your tenant. Users will have a grace period to enroll after policy changes.
          </p>
          <form onSubmit={handlePolicyUpdate} className="settings-form">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={policyFormData.mfa_required_for_admins}
                  onChange={(e) => setPolicyFormData({ ...policyFormData, mfa_required_for_admins: e.target.checked })}
                  disabled={mfaLoading}
                />
                Require MFA for Tenant Admins
              </label>
              <small className="form-help">
                When enabled, all tenant administrators must have MFA enabled to access the system.
              </small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={policyFormData.mfa_required_for_users}
                  onChange={(e) => setPolicyFormData({ ...policyFormData, mfa_required_for_users: e.target.checked })}
                  disabled={mfaLoading}
                />
                Require MFA for Tenant Users
              </label>
              <small className="form-help">
                When enabled, all tenant users (staff) must have MFA enabled to access the system.
              </small>
            </div>

            <div className="form-group">
              <label>Grace Period (Days)</label>
              <input
                type="number"
                min="0"
                value={policyFormData.mfa_grace_period_days}
                onChange={(e) => setPolicyFormData({ ...policyFormData, mfa_grace_period_days: parseInt(e.target.value) || 0 })}
                disabled={mfaLoading}
              />
              <small className="form-help">
                Number of days users have to enroll in MFA after the policy is enabled. Set to 0 for immediate enforcement.
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={mfaLoading}>
                {mfaLoading ? 'Saving...' : 'Save MFA Policy'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User MFA Devices Section */}
      <div className="form-section">
        <h3>Your MFA Devices</h3>
        <p className="settings-description">
          Manage your multi-factor authentication devices. Add devices to enhance your account security.
        </p>

        <div className="mfa-devices-section">
          {mfaDevices.length === 0 ? (
            <div className="portalr-emptyState">
              <div className="portalr-emptyStateIcon" aria-hidden="true">
                <Shield size={20} />
              </div>
              <div className="portalr-emptyStateTitle">No MFA devices enrolled</div>
              <p className="portalr-emptyStateDesc">
                Enroll an MFA device to secure access to your portal.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => setShowEnrollmentModal(true)}
                >
                  Enroll MFA device
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mfa-devices-list">
                {(Array.isArray(mfaDevices) ? mfaDevices : []).map((device) => (
                  <div key={device.id} className="mfa-device-card">
                    <div className="mfa-device-header">
                      <div className="mfa-device-info">
                        <h4>{device.name}</h4>
                        {device.is_primary && (
                          <span className="mfa-device-badge">Primary</span>
                        )}
                        {device.is_enabled ? (
                          <span className="mfa-device-status status-enabled">Enabled</span>
                        ) : (
                          <span className="mfa-device-status status-disabled">Disabled</span>
                        )}
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteDevice(device.id)}
                        disabled={mfaLoading}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                    {device.last_used_at && (
                      <div className="mfa-device-meta">
                        Last used: {new Date(device.last_used_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mfa-devices-actions">
                <button className="btn btn-primary" onClick={() => setShowEnrollmentModal(true)}>
                  <Shield size={18} />
                  Enroll New Device
                </button>
              </div>
            </>
          )}
        </div>

        {mfaDevices.length > 0 && (
          <div className="mfa-info-box">
            <AlertCircle size={16} />
            <p>
              <strong>Important:</strong> Keep your recovery codes safe. If you lose access to all your MFA devices, 
              you can use recovery codes to regain access to your account.
            </p>
          </div>
        )}
      </div>

      {/* MFA Enrollment Modal */}
      {showEnrollmentModal && (
        <MFAEnrollmentModal
          isOpen={showEnrollmentModal}
          onClose={() => setShowEnrollmentModal(false)}
          onSuccess={() => {
            fetchMFADevices();
            setShowEnrollmentModal(false);
            onSuccess('MFA device enrolled successfully.');
          }}
        />
      )}
    </div>
  );
};

